import Landing from "./Landing";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabase";
import Sidebar from "./components/Sidebar";
import BottomNav from "./components/BottomNav";
import Topbar from "./components/Topbar";
import Home from "./pages/Home";
import Messages from "./pages/Messages";
import Orders from "./pages/Orders";
import Contacts from "./pages/Contacts";
import Settings from "./pages/Settings";
import Support from "./pages/Support";
import Relances from "./pages/Relances";
import Login from "./pages/Login";
import Register from "./pages/Register";
import { useMetrics } from "./hooks/useData";

function TrialExpiredOverlay({ onGoToSettings }) {
  return (
    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
      <div className="text-center p-6">
        <div className="text-3xl mb-2">🔒</div>
        <div className="text-sm font-medium text-gray-700 mb-1">Abonnement requis</div>
        <div className="text-xs text-gray-400 mb-3">Choisissez un plan pour accéder</div>
        <button
          onClick={onGoToSettings}
          className="px-4 py-2 bg-orange-600 text-white text-xs rounded-xl hover:bg-orange-700"
        >
          Voir les plans →
        </button>
      </div>
    </div>
  );
}

function TrialExpiredBanner({ onGoToSettings }) {
  return (
    <div className="bg-gradient-to-r from-red-500 to-orange-500 px-4 py-3 flex items-center justify-between gap-4 flex-shrink-0">
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xl flex-shrink-0">⏰</span>
        <div>
          <div className="text-white font-medium text-sm">
            Votre période d'essai est terminée
          </div>
          <div className="text-red-100 text-xs">
            Hellodia ne répond plus à vos clients — choisissez un plan pour continuer.
          </div>
        </div>
      </div>
      <button
        onClick={onGoToSettings}
        className="flex-shrink-0 px-3 py-2 bg-white text-red-600 rounded-xl text-xs font-medium hover:bg-red-50 transition-colors whitespace-nowrap"
      >
        Choisir un plan →
      </button>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [currentPage, setCurrentPage] = useState("home");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [orgPlan, setOrgPlan] = useState('starter');
  const [businessMode, setBusinessMode] = useState('sales');
  const [trialExpired, setTrialExpired] = useState(false);
  const { metrics } = useMetrics();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    async function loadOrgSettings() {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (!u) return;
      const { data } = await supabase
        .from('organizations')
        .select('id, plan_name, subscription_plan, business_mode, is_trial, is_active, trial_expires_at')
        .eq('user_id', u.id)
        .single();
      if (data) {
        setOrgPlan(data.is_trial ? 'trial' : (data.plan_name || data.subscription_plan || 'starter'));
        setBusinessMode(data.business_mode || 'sales');

        // Vérifier si trial expiré
        if (data.is_trial && data.trial_expires_at) {
          const expired = new Date() > new Date(data.trial_expires_at);
          if (expired && data.is_active === false) {
            setTrialExpired(true);
            // Notifier sur WhatsApp
            fetch('https://smart-crm-production.up.railway.app/api/notify-trial-expired', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ orgId: data.id })
            }).catch(() => {});
          }
        }
      }
    }
    loadOrgSettings();
  }, []);

  const canRelance = ['pro', 'premium'].includes(orgPlan);

  // Pages accessibles même si trial expiré
  const freePages = ['settings'];

  const pages = {
    home:     <Home />,
    messages: <Messages />,
    support:  <Support />,
    orders:   <Orders />,
    contacts: <Contacts />,
    relances: <Relances />,
    settings: <Settings onLogout={onLogout} />,
  };

  function goToSettings() {
    setCurrentPage('settings');
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {!isMobile && (
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          isOpen={sidebarOpen}
          setIsOpen={setSidebarOpen}
          metrics={metrics}
          user={user}
          onLogout={onLogout}
          canRelance={canRelance}
          businessMode={businessMode}
        />
      )}
      <div className="flex flex-col flex-1 overflow-hidden">

        {/* Banner trial expiré */}
        {trialExpired && (
          <TrialExpiredBanner onGoToSettings={goToSettings} />
        )}

        <Topbar
          currentPage={currentPage}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isMobile={isMobile}
        />

        <main className={`flex-1 overflow-y-auto relative ${isMobile ? "p-4 pb-20" : "p-6"}`}>
          {/* Overlay sur les pages bloquées */}
          {trialExpired && !freePages.includes(currentPage) && (
            <TrialExpiredOverlay onGoToSettings={goToSettings} />
          )}
          {pages[currentPage] || <Home />}
        </main>

        {isMobile && (
          <BottomNav
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            metrics={metrics}
            canRelance={canRelance}
            businessMode={businessMode}
          />
        )}
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authPage, setAuthPage] = useState("login");
  const [loading, setLoading] = useState(true);
  const [showLanding, setShowLanding] = useState(true);

  const ensureOrganization = useCallback(async (u) => {
    if (!u) return;
    try {
      const { data: existingOrg } = await supabase
        .from('organizations')
        .select('id')
        .eq('user_id', u.id)
        .single();

      if (!existingOrg) {
        const trialExpires = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        const businessName = u.user_metadata?.business_name || 'Mon Business';
        await supabase
          .from('organizations')
          .insert({
            name: businessName,
            email: u.email,
            user_id: u.id,
            plan_name: 'trial',
            is_trial: true,
            is_active: true,
            trial_started_at: new Date().toISOString(),
            trial_expires_at: trialExpires,
            trial_messages_limit: 100,
            trial_messages_used: 0,
            messages_limit: 0,
            messages_used: 0,
            subscription_plan: 'trial',
            subscription_status: 'trial'
          });
      }
    } catch (error) {
      console.error('Erreur ensureOrganization:', error);
    }
  }, []);

  async function handleLogin(u) {
    setUser(u);
    ensureOrganization(u);
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        ensureOrganization(session.user);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        ensureOrganization(session.user);
      } else {
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [ensureOrganization]);

  async function handleLogout() {
    await supabase.auth.signOut();
    setUser(null);
    setShowLanding(true);
    setAuthPage("login");
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-orange-600 rounded-xl flex items-center justify-center mx-auto mb-3">
            <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
              <rect x="2" y="4" width="18" height="12" rx="3.5" fill="white"/>
              <path d="M8 16 L5 20 L13 16" fill="white"/>
              <circle cx="7" cy="10" r="1.4" fill="#C04828"/>
              <circle cx="11" cy="10" r="1.4" fill="#C04828"/>
              <circle cx="15" cy="10" r="1.4" fill="#C04828"/>
            </svg>
          </div>
          <p className="text-sm text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    if (showLanding && authPage === "login") {
      return (
        <Landing
          goToRegister={() => { setShowLanding(false); setAuthPage("register"); }}
          goToLogin={() => { setShowLanding(false); setAuthPage("login"); }}
        />
      );
    }
    if (authPage === "register") {
      return (
        <Register
          onRegister={() => setAuthPage("login")}
          goToLogin={() => { setShowLanding(false); setAuthPage("login"); }}
        />
      );
    }
    return (
      <Login
        onLogin={handleLogin}
        goToRegister={() => { setShowLanding(false); setAuthPage("register"); }}
      />
    );
  }

  return <Dashboard user={user} onLogout={handleLogout} />;
}

export default App;